import type { ComponentDocMeta, DocCategory } from "./types";

export const componentRegistry: ComponentDocMeta[] = [
  {
    slug: "button",
    name: "Button",
    category: "form",
    description:
      "Accessible action trigger with variants, sizes, loading state, motion, and automatic GTM click tracking via AsriUIProvider.",
    whenToUse: "Use for primary actions, form submits, and navigation triggers. Prefer ghost or outline for secondary actions.",
    related: ["input", "dialog", "link"],
  },
  {
    slug: "link",
    name: "Link",
    category: "navigation",
    description:
      "Accessible anchor with GTM click tracking, external-link detection, and new-tab affordances.",
    whenToUse:
      "Use for in-app navigation and outbound URLs. Pair with AsriUIProvider analytics for per-link GTM events.",
    related: ["button", "side-nav", "breadcrumb"],
  },
  {
    slug: "breadcrumb",
    name: "Breadcrumb",
    category: "navigation",
    description:
      "Navigation trail with optional back control, separators, and current-page semantics.",
    whenToUse:
      "Use in docs, settings, and nested views so users can orient themselves and go back quickly.",
    related: ["link", "side-nav", "page-layout"],
  },
  {
    slug: "menu",
    name: "Menu",
    category: "navigation",
    description:
      "Dropdown menu with trigger, items, groups, separators, and keyboard navigation.",
    whenToUse:
      "Use for action menus, overflow menus, and compact navigation panels triggered from a button.",
    related: ["button", "dialog", "side-nav", "context-menu"],
  },
  {
    slug: "tooltip",
    name: "Tooltip",
    category: "feedback",
    description:
      "Contextual label shown on hover or focus with configurable placement and delay.",
    whenToUse:
      "Use for icon-only controls, truncated labels, and short help text that should not clutter the layout.",
    related: ["button", "menu", "dialog"],
  },
  {
    slug: "dropdown",
    name: "Dropdown",
    category: "form",
    description:
      "Select-style dropdown for choosing a single value with keyboard navigation and option groups.",
    whenToUse:
      "Use for compact single-select fields such as country, status, or filter pickers.",
    related: ["input", "menu", "form"],
  },
  {
    slug: "date-picker",
    name: "DatePicker",
    category: "form",
    description:
      "Typeable date and datetime picker with range selection, min/max constraints, and disable-past support.",
    whenToUse:
      "Use for booking flows, reporting filters, and forms that need typed dates or date ranges.",
    related: ["input", "calendar", "form"],
  },
  {
    slug: "input",
    name: "Input",
    category: "form",
    description:
      "Labeled text field with helper/error messaging, affixes, and automatic ARIA relationships.",
    whenToUse: "Use for single-line text, email, password, and search fields. Pair with Label for custom layouts.",
    related: ["label", "form"],
  },
  {
    slug: "label",
    name: "Label",
    category: "form",
    description: "Accessible form label with optional required asterisk.",
    related: ["input", "switch"],
  },
  {
    slug: "switch",
    name: "Switch",
    category: "form",
    description: "WAI-ARIA switch toggle with controlled/uncontrolled modes.",
    related: ["label", "form"],
  },
  {
    slug: "form",
    name: "Form",
    category: "form",
    description: "JSON-configured form with built-in validation — Formik-style, zero extra deps.",
    whenToUse: "Use when forms are driven by CMS JSON, runtime config, or admin builders.",
    related: ["input", "label", "switch", "page"],
  },
  {
    slug: "page",
    name: "Page",
    category: "layout",
    description: "JSON-configured page builder — pass a page schema and render layout, forms, stats, tables, and more.",
    whenToUse: "CMS-driven screens, admin builders, or any UI you want to ship from serializable JSON.",
    related: ["form", "page-layout", "side-nav", "card", "tabs"],
  },
  {
    slug: "card-validation",
    name: "CardValidation",
    category: "form",
    description: "Payment card fields with Luhn validation, expiry/CVC rules, and brand detection.",
    whenToUse: "Checkout, billing, and any flow that collects card details client-side before tokenization.",
    related: ["form", "input", "button"],
  },
  {
    slug: "card",
    name: "Card",
    category: "layout",
    description: "Compound layout surface with Header, Title, Content, and Footer.",
    whenToUse: "Group related content in dashboards, settings panels, and billing summaries.",
    related: ["list-item", "skeleton", "widget", "metric"],
  },
  {
    slug: "widget",
    name: "Widget",
    category: "layout",
    description: "Drop-in embed for partner pages, ad scripts, and third-party widgets.",
    whenToUse: "Use `src` for iframe/webview content or `scriptSrc` for ad tags and vendor loaders — no manual page wiring required.",
    related: ["card", "container", "aspect-ratio"],
  },
  {
    slug: "metric",
    name: "Metric",
    category: "data",
    description: "Trading metric tiles for quotes, portfolio stats, and market summaries.",
    whenToUse: "Use for live prices, P&L, volume, and ticker rows in trading dashboards and portfolio views.",
    related: ["card", "data-grid", "badge"],
  },
  {
    slug: "separator",
    name: "Separator",
    category: "layout",
    description: "Horizontal or vertical divider for sections, menus, and toolbars.",
    related: ["card", "tabs", "menu"],
  },
  {
    slug: "callout",
    name: "Callout",
    category: "feedback",
    description: "Highlighted notice for info, success, warning, and danger messages.",
    related: ["badge", "card", "quote"],
  },
  {
    slug: "quote",
    name: "Quote",
    category: "layout",
    description: "Styled blockquote for testimonials, citations, and pull quotes.",
    related: ["typography", "callout", "card"],
  },
  {
    slug: "text-to-speech",
    name: "TextToSpeech",
    category: "layout",
    description: "Inline text with a speaker control that reads aloud via the Web Speech API.",
    whenToUse: "Use for accessibility, learning content, or hands-free reading of short passages.",
    related: ["typography", "button", "icon"],
  },
  {
    slug: "checkbox",
    name: "Checkbox",
    category: "form",
    description: "Accessible checkbox with optional label and description.",
    related: ["label", "switch", "checkbox-card"],
  },
  {
    slug: "radio",
    name: "Radio",
    category: "form",
    description: "Accessible radio input with `Radio.Group` for single-select fields.",
    related: ["label", "checkbox", "radio-card"],
  },
  {
    slug: "checkbox-card",
    name: "CheckboxCard",
    category: "form",
    description: "Selectable card surface for multi-select plans, features, and filters.",
    related: ["checkbox", "card", "radio-card"],
  },
  {
    slug: "radio-card",
    name: "RadioCard",
    category: "form",
    description: "Selectable card surface for pricing tiers, shipping methods, and single-choice settings.",
    related: ["radio", "card", "checkbox-card"],
  },
  {
    slug: "reset",
    name: "Reset",
    category: "form",
    description: "Scoped state reset for component design previews and configurable panels.",
    related: ["button", "form", "switch"],
  },
  {
    slug: "visible",
    name: "Visible",
    category: "layout",
    description: "Conditional show/hide primitives with optional animation via `Visible` and `Hidden`.",
    related: ["dialog", "tabs", "skeleton"],
  },
  {
    slug: "list-item",
    name: "ListItem",
    category: "layout",
    description: "Semantic list rows with media, descriptions, and interactive selection.",
    related: ["card", "skeleton"],
  },
  {
    slug: "grid",
    name: "Grid",
    category: "layout",
    description: "CSS grid with fixed column count or auto-fill responsive columns.",
    whenToUse: "Gallery layouts, card grids, and dashboard tiles.",
    related: ["container", "flex", "aspect-ratio", "card"],
  },
  {
    slug: "container",
    name: "Container",
    category: "layout",
    description: "Centered max-width wrapper with size and padding tokens for page content.",
    whenToUse: "Constrain readable content width on marketing pages, docs, and dashboards.",
    related: ["flex", "grid", "page-layout"],
  },
  {
    slug: "flex",
    name: "Flex",
    category: "layout",
    description: "Flexbox layout primitive with direction, align, justify, gap, and wrap props.",
    whenToUse: "Toolbars, stacks, split rows, and responsive wrapping clusters.",
    related: ["container", "grid", "page-layout"],
  },
  {
    slug: "theme-switch",
    name: "ThemeSwitch",
    category: "navigation",
    description: "Light/dark theme toggle with ripple and other View Transition animations.",
    whenToUse: "App chrome, docs shells, and settings panels that need an animated theme toggle.",
    related: ["button", "menu"],
  },
  {
    slug: "table",
    name: "Table",
    category: "layout",
    description: "Semantic compound table for manual markup with striped, bordered, and scrollable variants.",
    whenToUse: "Static reports, simple data displays, and custom row layouts when you do not need sorting or virtualization.",
    related: ["data-grid", "badge", "skeleton"],
  },
  {
    slug: "data-grid",
    name: "DataGrid",
    category: "advanced",
    description: "Sortable data grid with a native table engine or plug-and-play AG Grid integration.",
    whenToUse: "Admin tables, dashboards, and dense datasets. Use engine=\"native\" out of the box, or engine=\"ag-grid\" after installing ag-grid-community and ag-grid-react.",
    related: ["table", "virtual-list", "badge"],
  },
  {
    slug: "aspect-ratio",
    name: "AspectRatio",
    category: "layout",
    description: "Locks children to a width/height ratio — perfect for images and video.",
    related: ["image", "grid", "card"],
  },
  {
    slug: "image",
    name: "Image",
    category: "layout",
    description: "Lazy-loaded responsive image that builds srcSet variants from one base path.",
    whenToUse: "Hero images, galleries, and avatars with responsive loading.",
    related: ["aspect-ratio", "grid", "skeleton"],
  },
  {
    slug: "scroll-area",
    name: "ScrollArea",
    category: "layout",
    description: "Custom scrollbar for scrollable regions and full-page document scrolling.",
    whenToUse: "Long content panels, feeds, sidebars, and app shells where you want consistent styled scrollbars.",
    related: ["page-layout", "virtual-list", "side-nav"],
  },
  {
    slug: "page-layout",
    name: "PageLayout",
    category: "layout",
    description: "Configurable page shell with sidebar, docs, centered, and full-width presets.",
    whenToUse: "Wrap app pages and documentation sites. Pair with SideNav for sidebar navigation.",
    related: ["side-nav", "card", "hero"],
  },
  {
    slug: "hero",
    name: "Hero",
    category: "layout",
    description:
      "Compound landing hero with full-width copy or a split layout (text left or right), plus dotted, grid, glow, aurora, and mesh backgrounds.",
    whenToUse:
      "Marketing homepages and product launches. Use variant=\"full\" for a text-only band, or variant=\"split\" with textSide=\"right\" for copy beside a visual or Slider.",
    related: ["slider", "button", "page-layout"],
  },
  {
    slug: "slider",
    name: "Slider",
    category: "layout",
    description: "Compound carousel with track, slides, drag/swipe, previous/next controls, and dots.",
    whenToUse: "Hero media, product galleries, and testimonials. Pair with Hero.Media for a split hero.",
    related: ["hero", "image", "aspect-ratio"],
  },
  {
    slug: "side-nav",
    name: "SideNav",
    category: "navigation",
    description: "Sidebar navigation with left-border hover and active states.",
    whenToUse: "Documentation sidebars, settings panels, and dashboard navigation. Use Toggle with variant=\"hamburger\" and collapseMode=\"hidden\" for mobile-style hide/show.",
    related: ["page-layout", "tabs"],
  },
  {
    slug: "code-block",
    name: "CodeBlock",
    category: "layout",
    description: "VS Code-style syntax-highlighted code display with optional copy button.",
    whenToUse: "Documentation, API examples, and anywhere you show code snippets.",
    related: ["monaco-editor", "markdown"],
  },
  {
    slug: "markdown",
    name: "Markdown",
    category: "layout",
    description:
      "Lightweight Markdown renderer for docs, changelogs, and CMS snippets — headings, lists, tables, links, and fenced CodeBlock.",
    whenToUse:
      "Render README-style content, release notes, or help text without pulling in a markdown library.",
    related: ["code-block", "typography", "link"],
  },
  {
    slug: "badge",
    name: "Badge",
    category: "feedback",
    description: "Compact inline status label with four variants.",
    related: ["button", "card"],
  },
  {
    slug: "dialog",
    name: "Dialog",
    category: "feedback",
    description: "Portaled modal with overlay dismiss, escape key, and compound layout slots.",
    whenToUse: "Confirmations, forms, and focused tasks that require user attention.",
    related: ["button"],
  },
  {
    slug: "toast",
    name: "Toast",
    category: "feedback",
    description: "Animated toast notifications with success, error, warning, and info variants.",
    whenToUse: "Confirm saves, show errors, and surface async feedback without blocking the UI.",
    related: ["dialog", "button"],
  },
  {
    slug: "auth",
    name: "Auth",
    category: "form",
    description: "Login form and OAuth buttons for Microsoft, Google, GitHub, and Apple.",
    whenToUse: "Sign-in pages and auth modals. Wire onOAuth to your identity provider redirect.",
    related: ["form", "input", "button"],
  },
  {
    slug: "calendar",
    name: "Calendar",
    category: "data",
    description: "Booking calendar with month grid and multi-slot time selection per day.",
    whenToUse: "Appointment booking, scheduling UIs, and availability pickers with multiple slots per day.",
    related: ["dialog", "button", "badge"],
  },
  {
    slug: "skeleton",
    name: "Skeleton",
    category: "feedback",
    description: "Animated loading placeholders with shape variants and profile/card presets.",
    whenToUse: "Show while async data loads. Compose multiple skeletons for list and card layouts.",
    related: ["card", "list-item", "loader"],
  },
  {
    slug: "loader",
    name: "Loader",
    category: "feedback",
    description: "Accessible loading spinner with spinner, dots, and ring variants.",
    whenToUse: "Inline loading states, button submits, and full-page loading overlays.",
    related: ["skeleton", "button"],
  },
  {
    slug: "timeline",
    name: "Timeline",
    category: "layout",
    description: "Vertical timeline for roadmaps, changelogs, and multi-step progress.",
    whenToUse: "Product roadmaps, onboarding steps, order tracking, and release history.",
    related: ["card", "badge"],
  },
  {
    slug: "tabs",
    name: "Tabs",
    category: "navigation",
    description: "Compound tabbed panels with full WAI-ARIA tablist semantics.",
    related: ["list-item", "accordion"],
  },
  {
    slug: "accordion",
    name: "Accordion",
    category: "navigation",
    description: "Expandable sections with single or multiple selection, side content slots, and animated panels.",
    whenToUse: "FAQs, settings groups, nested disclosure panels, and filter sections.",
    related: ["tabs", "side-nav", "card"],
  },
  {
    slug: "virtual-list",
    name: "VirtualList",
    category: "data",
    description: "Fixed-height virtualized list with overscan and SSR-safe rendering.",
    whenToUse: "Lists with hundreds or thousands of rows where full DOM mount is too slow.",
    related: ["skeleton", "list-item"],
  },
  {
    slug: "error-boundary",
    name: "ErrorBoundary",
    category: "advanced",
    description: "React error boundary with fallback UI and remote monitoring reporting.",
    related: ["dialog"],
  },
  {
    slug: "monaco-editor",
    name: "MonacoEditor",
    category: "advanced",
    description: "Lazy-loaded Monaco editor for JSON and TypeScript configuration UIs.",
    related: ["form"],
  },
  {
    slug: "flow-chart",
    name: "FlowChart",
    category: "advanced",
    description: "React Flow wrapper with minimap and zoom controls.",
    related: ["card"],
  },
  {
    slug: "icon",
    name: "Icon",
    category: "layout",
    description: "Accessible inline SVG icons with size presets and optional labels.",
    whenToUse: "Use for UI affordances, feature lists, and navigation. Pass label when the icon conveys meaning alone.",
    related: ["button", "typography"],
  },
  {
    slug: "typography",
    name: "Typography",
    category: "layout",
    description: "Semantic text primitives — headings, body, lead, muted, and inline code.",
    whenToUse: "Establish consistent type scale across docs, dashboards, and marketing pages.",
    related: ["color-palette", "icon"],
  },
  {
    slug: "color-palette",
    name: "ColorPalette",
    category: "layout",
    description: "Accessible color swatch grid with copy-to-clipboard and contrast preview.",
    whenToUse: "Design system docs, theme pickers, and token reference pages.",
    related: ["typography", "icon"],
  },
  {
    slug: "ai-chat",
    name: "AiChat",
    category: "advanced",
    description: "Accessible AI chat shell with messages, suggestion chips, and prompt input.",
    whenToUse: "Copilot panels, assistant sidebars, and prompt UIs — wire your own model backend.",
    related: ["input", "button", "icon", "ai-workflow-builder"],
  },
  {
    slug: "ai-workflow-builder",
    name: "AiWorkflowBuilder",
    category: "advanced",
    description: "Visual builder for AI agent workflows with palette, canvas, inspector, and run controls.",
    whenToUse: "Design prompt chains, tool routing, and model steps before wiring your orchestration backend.",
    related: ["ai-chat", "ai-orchestrator", "flow-chart", "button"],
  },
  {
    slug: "ai-summarizer",
    name: "AiSummarizer",
    category: "advanced",
    description: "Condense long text into bullet or paragraph summaries from natural language workflows.",
    whenToUse: "Docs copilots, ticket triage, and meeting notes — wire onSummarize to your model.",
    related: ["ai-orchestrator", "ai-chat", "typography"],
  },
  {
    slug: "ai-data-analyst",
    name: "AiDataAnalyst",
    category: "advanced",
    description: "Natural-language queries rendered as KPI metrics, bar charts, and data tables.",
    whenToUse: "Self-serve analytics panels — pair onAnalyze with your SQL or BI backend.",
    related: ["data-grid", "metric", "ai-orchestrator"],
  },
  {
    slug: "ai-form-filler",
    name: "AiFormFiller",
    category: "advanced",
    description: "Map free-form instructions into structured form field values.",
    whenToUse: "Support intake, CRM updates, and onboarding — wire onFill to extraction models.",
    related: ["form", "ai-orchestrator", "input"],
  },
  {
    slug: "ai-search",
    name: "AiSearch",
    category: "advanced",
    description: "Semantic / natural-language search with scored results and snippets.",
    whenToUse: "Docs and knowledge bases — replace demo scoring with your vector index.",
    related: ["ai-orchestrator", "input", "badge"],
  },
  {
    slug: "ai-orchestrator",
    name: "AiOrchestrator",
    category: "advanced",
    description: "Unified shell with tabs for summarizer, analyst, form filler, search, and pipeline mode.",
    whenToUse: "Product copilots that route one prompt to multiple AI tools in sequence.",
    related: ["ai-summarizer", "ai-data-analyst", "ai-form-filler", "ai-search", "ai-chat"],
  },
  {
    slug: "context-menu",
    name: "ContextMenu",
    category: "navigation",
    description: "Custom right-click menu with items and separators, portaled at the cursor.",
    whenToUse: "Section actions, copy-layout shortcuts, and canvas tools where the native browser menu should be replaced.",
    related: ["menu", "dialog", "feature-request", "questionnaire"],
  },
  {
    slug: "feature-request",
    name: "FeatureRequest",
    category: "form",
    description: "Validated form for collecting a feature title, category, description, and optional email.",
    whenToUse: "Feedback widgets, product intake, and “add feature” flows from a context menu or settings page.",
    related: ["form", "questionnaire", "dialog"],
  },
  {
    slug: "questionnaire",
    name: "Questionnaire",
    category: "form",
    description: "Stepped survey with text, single-choice, and multiple-choice questions.",
    whenToUse: "Onboarding, research, and product discovery. Pass your own questions and persist onComplete.",
    related: ["form", "feature-request", "radio"],
  },
  {
    slug: "server-query",
    name: "ServerQuery",
    category: "data",
    description:
      "Fetch server data with built-in loading, error, and retry states — pass a query function or URL and render children with the result.",
    whenToUse:
      "Use for API-driven cards, dashboards, and page sections. Pair with Loader and ErrorBoundary for full async UX.",
    related: ["loader", "skeleton", "error-boundary", "data-grid"],
  },
];

export function getComponentMeta(slug: string): ComponentDocMeta | undefined {
  return componentRegistry.find((c) => c.slug === slug);
}

export function getComponentsByCategory(): Record<DocCategory, ComponentDocMeta[]> {
  const grouped = {} as Record<DocCategory, ComponentDocMeta[]>;
  for (const doc of componentRegistry) {
    if (!grouped[doc.category]) grouped[doc.category] = [];
    grouped[doc.category].push(doc);
  }
  return grouped;
}

/** @deprecated Use componentRegistry */
export const componentDocs = componentRegistry;
